import {getFinishSRPAuthParams, getStartSRPAuthParams} from 'app/common/authUtils';
import {BaseAPI, IOptions} from 'app/common/BaseAPI';

export interface VerifyResult {
  // Whether verification code was confirmed or resent.
  status: 'confirmed' | 'resent';
}

/**
 * User multi-factor authentication preferences, as fetched from Cognito.
 */
 export interface UserMFAPreferences {
  isSmsMfaEnabled: boolean;
  // If SMS MFA is enabled, the destination number for receiving verification codes.
  phoneNumber?: string;
  isSoftwareTokenMfaEnabled: boolean;
}

/**
 * Cognito response to initiating software token MFA registration.
 */
 export interface SoftwareTokenRegistrationInfo {
  secretCode: string;
}

/**
 * Cognito response to initiating SMS MFA registration.
 */
export interface SMSRegistrationInfo {
  deliveryDestination: string;
}

interface StartSRPAuthResponse {
  userId: string,
  serverBValue: string,
  salt: string,
  secretBlock: string,
  poolName: string,
}

/**
 * Cognito response to verifying a password (e.g. in a security verification form).
 */
export type VerifyPasswordResponse = ChallengeRequired | ChallengeNotRequired;

/**
 * Information about the follow-up authentication challenge.
 */
export interface ChallengeRequired {
  isChallengeRequired: true;
  isAlternateChallengeAvailable: boolean;
  // Session identifier that must be re-used in response to auth challenge.
  session: string;
  username: string;
  challengeName: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';
  // If SMS MFA is enabled, the destination phone number that codes are sent to.
  deliveryDestination?: string;
}

/**
 * Successful authentication, with no additional challenge required.
 */
interface ChallengeNotRequired {
  isChallengeRequired: false;
}

/**
 * Cognito response to SRP-based login.
 */
export type LoginResponse = ChallengeRequired | LoginSuccess;

export interface LoginSuccess extends ChallengeNotRequired {
  // Post-login URL that client should redirect to.
  redirectUrl: string;
}

export type AuthMethod = 'TOTP' | 'SMS';

export interface LogInOptions {
  preferredMfaMethod?: AuthMethod;
  state?: string;
}

export interface LogInSecondStepParams {
  username: string;
  authMethod: AuthMethod;
  verificationCode: string;
  session: string;
  state?: string;
}

export interface ResetPasswordResponse {
  username: string;
}

export interface LoginAPI {
  signUp(email: string, password: string, fullName?: string): Promise<void>;
  confirmSignUp(email: string, code: string): Promise<VerifyResult>;
  logIn(email: string, password: string, options?: LogInOptions): Promise<LoginResponse>;
  logInSecondStep(params: LogInSecondStepParams): Promise<LoginSuccess>;
  resetPassword(email: string): Promise<ResetPasswordResponse>;
  confirmResetPassword(username: string, password: string, code: string): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  getUserMfaPreferences(): Promise<UserMFAPreferences>;
  registerSoftwareToken(): Promise<SoftwareTokenRegistrationInfo>;
  confirmRegisterSoftwareToken(verificationCode: string): Promise<void>;
  unregisterSoftwareToken(): Promise<void>;
  registerSMS(phoneNumber: string): Promise<SMSRegistrationInfo>;
  confirmRegisterSMS(verificationCode: string): Promise<void>;
  unregisterSMS(): Promise<void>;
  verifyPassword(password: string, preferredMfaMethod?: AuthMethod): Promise<VerifyPasswordResponse>;
  verifySecondStep(authMethod: AuthMethod, verificationCode: string, session: string): Promise<void>;
  getGoogleSignInUrl(state?: string): string;
}

export class LoginAPIImpl extends BaseAPI implements LoginAPI {
  constructor(private _loginUrl: string, options: IOptions = {}) {
    super(options);
  }

  public async signUp(email: string, password: string, fullName?: string): Promise<void> {
    await this.request(`${this._url}/signup/register`, {
      method: 'POST',
      body: JSON.stringify({email, password, fullName}),
    });
  }

  public async confirmSignUp(email: string, code: string): Promise<VerifyResult> {
    return this.requestJson(`${this._url}/signup/verify`, {
      method: 'POST',
      body: JSON.stringify({email, code}),
    });
  }

  public async logIn(email: string, password: string, options: LogInOptions = {}): Promise<LoginResponse> {
    const {authHelper, clientAValue} = await getStartSRPAuthParams();
    const startAuthResponse: StartSRPAuthResponse = await this.requestJson(`${this._url}/api/auth/start-login`, {
      method: 'POST',
      body: JSON.stringify({clientAValue, email}),
    });

    const finishAuthParams = await getFinishSRPAuthParams(authHelper, {...startAuthResponse, password});
    const finishLoginUrl = new URL(`${this._url}/api/auth/finish-login`);
    const {preferredMfaMethod, state} = options;
    if (state) { finishLoginUrl.searchParams.set('state', state); }
    return await this.requestJson(finishLoginUrl.href, {
      method: 'POST',
      body: JSON.stringify({...finishAuthParams, preferredMfaMethod, username: startAuthResponse.userId}),
    });
  }

  public async logInSecondStep({
    username,
    verificationCode,
    authMethod,
    session,
    state
  }: LogInSecondStepParams): Promise<LoginSuccess> {
    const url = new URL(`${this._url}/api/auth/login-second-step`);
    if (state) { url.searchParams.set('state', state); }
    return await this.requestJson(url.href, {
      method: 'POST',
      body: JSON.stringify({username, authMethod, verificationCode, session}),
    });
  }

  public async resetPassword(email: string): Promise<ResetPasswordResponse> {
    return this.requestJson(`${this._url}/api/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({email}),
    });
  }

  public async confirmResetPassword(username: string, password: string, code: string): Promise<void> {
    await this.request(`${this._url}/api/auth/confirm-reset-password`, {
      method: 'POST',
      body: JSON.stringify({username, password, code}),
    });
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request(`${this._url}/api/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({currentPassword, newPassword}),
    });
  }

  public async getUserMfaPreferences(): Promise<UserMFAPreferences> {
    return this.requestJson(`${this._url}/api/profile/mfa_preferences`);
  }

  public async registerSoftwareToken(): Promise<SoftwareTokenRegistrationInfo> {
    return this.requestJson(`${this._url}/api/auth/register_totp`, {method: 'POST'});
  }

  public async confirmRegisterSoftwareToken(verificationCode: string): Promise<void> {
    await this.request(`${this._url}/api/auth/confirm_register_totp`, {
      method: 'POST',
      body: JSON.stringify({verificationCode}),
    });
  }

  public async unregisterSoftwareToken(): Promise<void> {
    await this.request(`${this._url}/api/auth/unregister_totp`, {method: 'POST'});
  }

  public async registerSMS(phoneNumber: string): Promise<SMSRegistrationInfo> {
    return this.requestJson(`${this._url}/api/auth/register_sms`, {
      method: 'POST',
      body: JSON.stringify({phoneNumber}),
    });
  }

  public async confirmRegisterSMS(verificationCode: string): Promise<void> {
    await this.request(`${this._url}/api/auth/confirm_register_sms`, {
      method: 'POST',
      body: JSON.stringify({verificationCode}),
    });
  }

  public async unregisterSMS(): Promise<void> {
    await this.request(`${this._url}/api/auth/unregister_sms`, {method: 'POST'});
  }

  public async verifyPassword(password: string, preferredMfaMethod?: AuthMethod): Promise<VerifyPasswordResponse> {
    const {authHelper, clientAValue} = await getStartSRPAuthParams();
    const startAuthResponse: StartSRPAuthResponse = await this.requestJson(`${this._url}/api/auth/verify-password`, {
      method: 'POST',
      body: JSON.stringify({clientAValue}),
    });

    const finishAuthParams = await getFinishSRPAuthParams(authHelper, {...startAuthResponse, password});
    return await this.requestJson(`${this._url}/api/auth/finish-verify-password`, {
      method: 'POST',
      body: JSON.stringify({...finishAuthParams, preferredMfaMethod}),
    });
  }

  public async verifySecondStep(
    authMethod: AuthMethod,
    verificationCode: string,
    session: string
  ): Promise<void> {
    await this.request(`${this._url}/api/auth/verify_second_step`, {
      method: 'POST',
      body: JSON.stringify({authMethod, verificationCode, session}),
    });
  }

  public getGoogleSignInUrl(state?: string): string {
    const url = new URL(`${this._url}/auth/google-sign-in`);
    if (state) { url.searchParams.set('state', state); }
    return url.href;
  }

  private get _url(): string {
    return this._loginUrl.replace(/\/$/, '');
  }
}
